/**
 * useBlueprintAnalytics — typed analytics hook for the Blueprints hub.
 *
 * ## Event coverage
 *
 * ### blueprint_card_click
 * Emitted when the user clicks "Choose" on a template card.
 * Payload attributes: `{ category: string }`.
 * Subject: the `templateRef` (e.g. `template:default/add-helm-chart`).
 *
 * ### blueprint_choose_start
 * Emitted at the same moment as `blueprint_card_click` — i.e. when the
 * user clicks "Choose" and is navigated to the wizard.
 *
 * @backstage/plugin-scaffolder v1.36.1 does NOT expose per-wizard-step
 * callbacks via `RouterProps`, `TemplateWizardPageProps`, or any other
 * public API surface (verified against dist/index.d.ts). Therefore
 * per-step events (`blueprint_choose_step`) are not emittable from the
 * Templates tab without undocumented internal hooks. We emit only
 * `blueprint_choose_start` and document the gap here.
 *
 * ### blueprint_task_outcome
 * Exported from this hook but NOT called from `BlueprintsTemplatesTab`.
 *
 * Rationale: the actual `scaffold()` call happens inside `ScaffolderPage`'s
 * internal router (the wizard), which our Templates tab wraps but does not
 * control. The taskId is unavailable to our `handleChoose` callback.
 * `streamLogs({ taskId })` returns an `Observable<LogEvent>` with
 * `LogEvent.type === 'completion'` as the terminal signal — the right
 * plumbing exists, but it must be called with a taskId we don't have here.
 *
 * Correct placement for a follow-up: call `trackTaskOutcome` from
 * `TasksListPage.tsx`'s polling loop when a task transitions from a
 * non-terminal status (`open` | `processing`) to a terminal one
 * (`completed` | `failed` | `cancelled` | `skipped`) between successive
 * `scaffolderApi.listTasks()` calls. This is explicitly out of T9's scope
 * and deferred per task rules.
 *
 * ### blueprint_filter_zero_results
 * Emitted when the filtered template count is 0, debounced 500 ms.
 * Payload attributes: `{ query, tags, viewMode }` where `tags` is the
 * comma-joined, sorted list of selected tag filters.
 * The debounce and dedup logic live in the component (`BlueprintsTemplatesTab`)
 * because `useEffect` + `useRef` are simpler there than inside the hook.
 */

import { useCallback } from 'react';
import { useAnalytics } from '@backstage/core-plugin-api';

// ── Public interface ──────────────────────────────────────────────────────────

export interface BlueprintAnalyticsCallbacks {
  /**
   * Call when the user clicks "Choose" on a template card.
   * @param templateRef - entity ref, e.g. `template:default/add-helm-chart`
   * @param category    - first curated category tag on the template, or `'other'`
   */
  trackCardClick(templateRef: string, category: string): void;

  /**
   * Call at the start of the choose wizard flow (same moment as card click).
   * Per-step events are not available — see module-level comment.
   */
  trackChooseStart(templateRef: string): void;

  /**
   * Call when a scaffolder task reaches a terminal status.
   * NOTE: not wired from BlueprintsTemplatesTab — see module-level comment.
   */
  trackTaskOutcome(input: {
    templateRef: string;
    status: 'success' | 'failure';
    durationMs: number;
  }): void;

  /**
   * Call when the filtered template grid yields zero results (after debounce).
   */
  trackZeroResults(input: {
    query: string;
    tags: string;
    viewMode: 'grid' | 'list';
  }): void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Returns stable analytics callbacks for the Blueprints hub.
 *
 * All callbacks are memoised with `useCallback` so they are safe to include
 * in dependency arrays without triggering spurious effect re-runs.
 *
 * The underlying `useAnalytics()` hook gracefully no-ops when no
 * `analyticsApiRef` factory is registered (e.g. in unit tests that don't
 * configure the provider), so this hook is safe to call unconditionally.
 */
export function useBlueprintAnalytics(): BlueprintAnalyticsCallbacks {
  // useAnalytics() is safe to call without a registered provider:
  // it falls back to a no-op captureEvent when the API is absent.
  const analytics = useAnalytics();

  const trackCardClick = useCallback(
    (templateRef: string, category: string) => {
      analytics.captureEvent('blueprint_card_click', templateRef, {
        attributes: { category },
      });
    },
    [analytics],
  );

  const trackChooseStart = useCallback(
    (templateRef: string) => {
      analytics.captureEvent('blueprint_choose_start', templateRef);
    },
    [analytics],
  );

  const trackTaskOutcome = useCallback(
    (input: {
      templateRef: string;
      status: 'success' | 'failure';
      durationMs: number;
    }) => {
      analytics.captureEvent('blueprint_task_outcome', input.templateRef, {
        attributes: {
          status: input.status,
          // GA4 / AnalyticsEventAttributes allows `number` values
          durationMs: input.durationMs,
        },
      });
    },
    [analytics],
  );

  const trackZeroResults = useCallback(
    (input: { query: string; tags: string; viewMode: 'grid' | 'list' }) => {
      analytics.captureEvent(
        'blueprint_filter_zero_results',
        'templates-grid',
        {
          attributes: {
            query: input.query,
            tags: input.tags,
            viewMode: input.viewMode,
          },
        },
      );
    },
    [analytics],
  );

  return {
    trackCardClick,
    trackChooseStart,
    trackTaskOutcome,
    trackZeroResults,
  };
}
